<template>
  <div class="signature-box">
    <template v-if="type === 'draw'">
      <canvas :class="`signature-box__canvas ${isDisabled ? 'is-disabled' : ''}`"></canvas>
      <div class="signature-box__canvas__buttons">
        <a href="javascript:;" class="signature-box__canvas__clear"
          v-if="drawnSignature"
          @click="clearCanvas">
            <icon name="trash" height="16" width="16"></icon>
            <span>{{ $t('signatureBox.clear') }}</span>
          </a>
      </div>
      <message v-if="isDisabled" type="warning" iconName="warning">
        {{ $t('signatureBox.clearFirst') }}
      </message>
    </template>
    <template v-if="type === 'type'">
      <div class="signature-box__type">
        <input ref='input'
          @input="typeSignature" :value="typedSignature" type="text"/>
        <div class="signature-box__type__input">{{ typedSignature ? typedSignature : $t('signatureBox.yourSignature') }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import * as Sentry from '@sentry/browser'
import SignaturePad from 'signature_pad/dist/signature_pad'
import Message from '@/components/Message'

export default {
  name: 'signature-box',
  props: ['type', 'currentField'],
  components: {
    Message
  },
  data () {
    return {
      drawnSignature: this.$store.state.signatures.drawn,
      typedSignature: this.$store.state.signatures.typed,
      uploadedSignature: this.$store.state.signatures.uploaded,
      isDisabled: false,
      opts: {
        dragging: false
      },
      canvas: null,
      signaturePad: null,
      canvasHasRendered: false
    }
  },
  created () {
    this.$bus.$on('confirmSignatureField', (fieldId) => {
      if (!(this.currentField.upload || this.currentField.drawn_json || this.currentField.content) && fieldId === this.currentField.id) {
        if (this.drawnSignature && this.signaturePad) {
          const svgSignature = this.signaturePad.toDataURL('image/svg+xml')
          const jpgSignature = this.signaturePad.toDataURL('image/jpeg')

          if (jpgSignature === 'data:,') { // To try to capture events to help diagnose https://gitlab.wearedomo.com/domo/signable/signing-page/-/merge_requests/116
            alert('Oops! An error occured saving your signature.\n\nPlease describe what happened on the following screen and contact Signable support if this happens again.')

            Sentry.showReportDialog({
              eventId: Sentry.captureException(new Error('JPEG data is blank (confirm signature)'), {
                extra: {
                  signaturePad: this.signaturePad,
                  signaturePadCanvas: this.signaturePad._canvas,
                  componentData: this.$data
                }
              })
            })

            this.$bus.$emit('updateSignature', null, 'drawn_json')
          } else {
            this.$bus.$emit('updateSignature', [this.drawnSignature, svgSignature, jpgSignature], 'drawn_json')
          }
        } else if (this.typedSignature) {
          this.$bus.$emit('updateSignature', this.typedSignature)
        }
      }
    })
  },
  mounted () {
    this.whenReady()
  },
  watch: {
    'currentField.id': {
      handler () {
        window.removeEventListener('resize', this.redrawCanvas)
        this.signaturePad = null
        this.whenReady()
      },
      deep: true
    }
  },
  methods: {
    whenReady () {
      if ((this.type === 'draw' && !this.canvasHasRendered) || (this.type === 'draw' && !this.signaturePad)) {
        this.canvas = this.$el.querySelector('.signature-box__canvas')
        this.signaturePad = new SignaturePad(this.canvas, {
          backgroundColor: 'rgb(255, 255, 255)',
          onEnd: this.stoppedDrawing
        })
        this.redrawCanvas()
        window.addEventListener('resize', this.redrawCanvas)
        this.$nextTick(() => {
          this.redrawCanvas()
        })
      }

      this.$nextTick(() => {
        this.setSignaturesFromState()
      })
    },
    checkSignatures () {
      const state = this.$store.state.signatures
      const hasTyped = state.typed !== null
      const hasDrawn = state.drawn !== null
      const hasUpload = state.uploaded !== null
      if (!hasDrawn && !hasUpload && !hasTyped) {
        return false
      }
      const signatures = {
        typed: hasTyped,
        drawn: hasDrawn,
        uploaded: hasUpload
      }
      return signatures
    },
    stoppedDrawing () {
      this.drawnSignature = this.signaturePad.toData()
      const svgSignature = this.signaturePad.toDataURL('image/svg+xml')
      const jpgSignature = this.signaturePad.toDataURL('image/jpeg')

      if (jpgSignature === 'data:,') { // To try to capture events to help diagnose https://gitlab.wearedomo.com/domo/signable/signing-page/-/merge_requests/116
        Sentry.captureException(new Error('JPEG data is blank (stopped drawing)'), {
          extra: {
            signaturePad: this.signaturePad,
            signaturePadCanvas: this.signaturePad._canvas,
            componentData: this.$data
          }
        })
      }

      this.$bus.$emit('updateSignature', [this.drawnSignature, svgSignature, jpgSignature], 'drawn_json')
    },
    // TODO use a watcher here instead
    typeSignature (event) {
      this.$bus.$emit('updateSignature', event.target.value)
      if (!event.target.value || event.target.value.trim().length < 1) {
        this.typedSignature = null
      } else {
        this.typedSignature = event.target.value
      }
    },
    clearCanvas () {
      this.drawnSignature = null
      if (this.currentField.drawn_json) this.$bus.$emit('updateSignature', null, 'drawn_json')
      this.signaturePad.clear()
      this.signaturePad.off()
      this.signaturePad.on()
      this.isDisabled = false
    },
    setSignaturesFromState () {
      this.typedSignature = this.currentField.content
      this.uploadedSignature = this.currentField.upload
      this.drawnSignature = this.currentField.drawn_json ? this.currentField.drawn_json[0] : null

      const latestSignature = this.$store.getters.latestSignature

      if (latestSignature && !this.typedSignature && !this.drawnSignature && !this.uploadedSignature) {
        this.typedSignature = latestSignature.content ? latestSignature.content : null
        this.drawnSignature = latestSignature.drawn_json ? latestSignature.drawn_json[0] : null
        this.uploadedSignature = latestSignature.upload ? latestSignature.upload : null
      }

      if (this.signaturePad && this.drawnSignature) {
        this.isDisabled = true
        this.signaturePad.fromData(this.drawnSignature)
        this.signaturePad.off()
      }
    },
    redrawCanvas () {
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      this.canvas.width = this.canvas.offsetWidth * ratio
      this.canvas.height = this.canvas.offsetHeight * ratio
      this.canvas.getContext('2d').scale(ratio, ratio)
      this.canvasHasRendered = true
      this.signaturePad.clear()
      this.setSignaturesFromState()
    }
  }
}
</script>

<style lang="sass" scoped>
  @import '@/styles/component-utils.sass'
  .signature-box
    position: relative
    &__canvas
      border-bottom: 4px solid $color-border-dark
      width: 100%
      height: 250px
      display: block
      &__clear
        position: absolute
        top: 0
        right: 0
        background: $color-cancel
        padding: $spacing-small
        color: #fff
        border-radius: 3px
        text-decoration: none
        svg
          float: left
    &__type
      &__input
        font-family: 'danielregular'
        font-size: $font-size-heading
        border-top: 1px solid $color-border-dark
        padding: $spacing-base
      input
        width: 100%
        border: 0
        margin: 0
        display: block
        padding: $spacing-base
        font-size: $font-size-heading
    &__upload
      display: block
      border: 3px dashed $color-border-dark
      padding: $spacing-base
      min-height: 250px
      position: relative
      cursor: pointer
      &__icon
        position: absolute
        top: 50%
        left: 50%
        margin-right: 0
        margin-left: -150px
        margin-top: -50px
        text-align: center
        width: 300px
        height: 100px
      svg
        fill: $color-border-dark
      p
        margin: 0
        color: $color-border-dark
        font-weight: bold
      input
        display: none
      &--dragging
        border-color: darken($color-border-dark, 20%)
        svg
          fill: darken($color-border-dark, 20%)
        p
          color: darken($color-border-dark, 20%)
    &__image-placeholder
      border: 1px solid $color-border-dark
      position: relative
      min-height: 250px
      overflow: hidden
      svg
        margin-right: 0
        position: absolute
        left: 50%
        top: 50%
        margin-left: -30px
        margin-top: -30px
      img
        position: absolute
        top: 50%
        transform: translate3d(0, -50%, 0)
    /deep/ .message
      padding: $spacing-small
      margin-top: $spacing-base
      border: 1px solid $color-warning
      background-color: #fff
      color: $color-warning
  // Responsive
  @media screen and (max-width: $screen-size-small)
    .signature-box
      &__canvas
        height: 100px
      &__type
        &__input
          display: none
        input
          font-family: 'danielregular'
          padding: $spacing-small
          font-size: 25px
          margin: -$spacing-base
          width: 100%
  @media all and (max-width: $screen-size-medium) and (orientation : landscape)
    .signature-box
      &__canvas
        height: 100px
      &__type
        &__input
          display: none
        input
          font-family: 'danielregular'
          padding: $spacing-small
          font-size: 25px
          margin: -$spacing-base
          width: 100%
</style>
